import Axios, {
  AxiosInstance,
  AxiosRequestConfig,
  CustomParamsSerializer
} from 'axios';
import { getToken } from '@/utils/auth';
import { get } from 'lodash-es';
import {
  PureHttpError,
  PureHttpResponse,
  PureHttpRequestConfig
} from './types';
import { stringify } from 'qs';
import NProgress from '../progress';
import { message } from '@/utils/message';
import { ElMessageBox } from 'element-plus';
import { useUserStoreHook } from '@/store/modules/user';
import { BASE_URL } from './config';
import { usePermissionStoreHook } from '@/store/modules/permission';
import { $t } from '@/plugins/i18n';

// 是否显示重新登录
export const isReLogin = { show: false };
// 用于处理时区 :30 类型
function padLeft(str: string | number, numChars = 4, char = ' ') {
  return (Array.from({ length: numChars }).fill(char).join('') + str).slice(
    -1 * numChars
  );
}
const timezoneVal: number = 0 - new Date().getTimezoneOffset() / 60;
const numberFlag =
  timezoneVal && typeof timezoneVal === 'number' && timezoneVal % 1 === 0;
const timezone =
  'GMT' +
  (timezoneVal > 0 ? '+' : '-') +
  (numberFlag
    ? Math.abs(timezoneVal)
    : `${padLeft(Math.abs(timezoneVal), 2, '0')}${
        (Math.abs(timezoneVal) % 1) * 60
      }`);
// const language: string = localStorage.getItem('lang');
let lang = localStorage.getItem('lang');
const nlang = lang;
// 特殊模块的国际化，后续请统一使用langType来处理
if (lang === 'CN') {
  lang = 'zh_CN';
} else if (lang === 'EN') {
  lang = 'en_US';
} else if (lang === 'PH') {
  lang = 'en_US';
}

// 相关配置请参考：www.axios-js.com/zh-cn/docs/#axios-request-config-1
const defaultConfig: AxiosRequestConfig = {
  // 请求超时时间
  timeout: 30000,
  // 基本请求地址
  baseURL: BASE_URL,
  headers: {
    timezone,
    lang,
    langType: nlang
    //   Accept: "application/json, text/plain, */*",
    //   "Content-Type": "application/json",
    //   "X-Requested-With": "XMLHttpRequest"
  },
  // 数组格式参数序列化（https://github.com/axios/axios/issues/5142）
  paramsSerializer: {
    serialize: stringify as unknown as CustomParamsSerializer
  }
};
class PureHttp {
  constructor() {
    this.httpInterceptorsRequest();
    this.httpInterceptorsResponse();
  }

  /** token过期后，暂存待执行的请求 */
  private static requests = [];

  /** 初始化配置对象 */
  private static initConfig: PureHttpRequestConfig = {};

  /** 保存当前Axios实例对象 */
  axiosInstance: AxiosInstance = Axios.create(defaultConfig);

  /** 请求拦截 */
  private httpInterceptorsRequest(): void {
    this.axiosInstance.interceptors.request.use(
      async (config: PureHttpRequestConfig) => {
        // 开启进度条动画
        NProgress.start();
        // 优先判断post/get等方法是否传入回掉，否则执行初始化设置等回掉
        if (typeof config.beforeRequestCallback === 'function') {
          config.beforeRequestCallback(config);
          return config;
        }
        if (PureHttp.initConfig.beforeRequestCallback) {
          PureHttp.initConfig.beforeRequestCallback(config);
          return config;
        }
        /** 请求白名单，放置一些不需要token的接口（通过设置请求白名单，防止token过期后再请求造成的死循环问题） */
        const whiteList = ['/refreshToken', '/login'];
        return whiteList.some(v => config.url.indexOf(v) > -1)
          ? config
          : new Promise(resolve => {
              const data = getToken();
              if (data) {
                // @ts-ignore
                config.headers['authToken'] = data;
                const routeUrl = location.pathname;
                config.headers['routePath'] = routeUrl;
                resolve(config);
              } else {
                resolve(config);
              }
            });
      },
      error => {
        return Promise.reject(error);
      }
    );
  }

  /** 响应拦截 */
  private httpInterceptorsResponse(): void {
    const instance = this.axiosInstance;
    instance.interceptors.response.use(
      (response: PureHttpResponse) => {
        const $config = response.config;
        const res = response.data;
        // 关闭进度条动画
        NProgress.done();
        // 优先判断post/get等方法是否传入回掉，否则执行初始化设置等回掉
        if (typeof $config.beforeResponseCallback === 'function') {
          $config.beforeResponseCallback(response);
          return res;
        }
        if (PureHttp.initConfig.beforeResponseCallback) {
          PureHttp.initConfig.beforeResponseCallback(response);
          return res;
        }
        if (res.code !== 1 && response.config?.responseType !== 'arraybuffer') {
          if (res.code === 10000000) {
            setTimeout(() => {
              isReLogin.show = false;
              usePermissionStoreHook().clearAllCachePage();
              useUserStoreHook().logOut();
              window.location.reload();
            }, 3000);
          }
          message(res.msg, {
            type: 'error',
            duration: 5 * 1000
          });
          return Promise.reject(res.msg);
        }
        return response.data;
      },
      (error: PureHttpError) => {
        console.error('http request', error); // for debug
        const status = get(error, 'response.status');
        switch (status) {
          case 400:
            error.message = $t('请求错误');
            break;
          case 401:
            error.message = $t('token过期，请重新登陆！');
            break;
          case 403:
            error.message = $t('拒绝访问');
            break;
          case 404:
            error.message = `${$t('请求地址出错')}: ${
              error.response.config.url
            }`;
            break;
          case 408:
            error.message = $t('请求超时');
            break;
          case 500:
            error.message = $t('服务器内部错误');
            break;
          case 501:
            error.message = $t('服务未实现');
            break;
          case 502:
            error.message = $t('网关错误');
            break;
          case 503:
            error.message = $t('服务不可用');
            break;
          case 504:
            error.message = $t('网关超时');
            break;
          case 505:
            error.message = $t('HTTP版本不受支持');
            break;
          default:
            break;
        }
        message(error.message, {
          type: 'error',
          duration: 5 * 1000
        });
        if (error.response.status === 401) {
          if (!isReLogin.show) {
            isReLogin.show = true;
            ElMessageBox.confirm(
              $t('登录状态已过期，您可以继续留在该页面，或者重新登录'),
              $t('系统提示'),
              {
                confirmButtonText: $t('重新登录'),
                cancelButtonText: $t('取消'),
                type: 'warning'
              }
            )
              .then(() => {
                isReLogin.show = false;
                usePermissionStoreHook().clearAllCachePage();
                useUserStoreHook().logOut();
                window.location.reload();
              })
              .catch(() => {
                isReLogin.show = false;
              });
          }
        }
        // if (error.response.status === 403) {
        //   if (!isReLogin.show) {
        //     isReLogin.show = true;
        //     ElMessageBox.confirm(error.response.data.msg, $t('系统提示'), {
        //       confirmButtonText: $t('重新登录'),
        //       cancelButtonText: $t('取消'),
        //       type: 'warning'
        //     })
        //       .then(() => {
        //         isReLogin.show = false;
        //         useUserStoreHook().logOut();
        //       })
        //       .catch(() => {
        //         isReLogin.show = false;
        //       });
        //   }
        // }
        return Promise.reject(error);
      }
    );
  }
}

export default new PureHttp().axiosInstance;
